import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { clsx } from 'clsx'
import { useState, useEffect } from 'react'
import {
  LayoutDashboard,
  ShoppingBag,
  Users,
  Car,
  Building2,
  Settings,
  User,
  Car as CarLogo,
  BarChart3,
  ChevronDown,
  FileImage,
  Receipt,
  HandCoins,
  CalendarRange,
} from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import type { Tenant } from '@/types'
import Header from './Header'
import BottomNav from './BottomNav'

type NavItem = { label: string; icon: React.ElementType; path: string; key: string }
type NavGroup = { label: string; icon: React.ElementType; key: string; children: NavItem[] }
type NavEntry = NavItem | NavGroup

function isGroup(e: NavEntry): e is NavGroup { return 'children' in e }

const allNavigation: NavEntry[] = [
  { label: '儀表板',   icon: LayoutDashboard, path: '/dashboard', key: 'dashboard' },
  { label: '派車單管理', icon: ShoppingBag,     path: '/orders',   key: 'orders'    },
  { label: '我的支出', icon: Receipt,          path: '/my-expenses', key: 'my-expenses' },
  { label: '我的報表', icon: BarChart3,        path: '/my-report',   key: 'my-report' },
  { label: '司機管理', icon: Car,              path: '/drivers',  key: 'drivers'   },
  { label: '客戶管理', icon: Users,            path: '/customers',key: 'customers' },
  { label: '司機支出', icon: Receipt,          path: '/expenses', key: 'expenses'  },
  { label: '批次管理', icon: CalendarRange,    path: '/batch-orders', key: 'batch-orders' },
  {
    label: '報表管理', icon: BarChart3, key: 'reports',
    children: [
      { label: '司機報表', icon: Car,   path: '/reports/drivers',   key: 'report-drivers'   },
      { label: '客戶報表', icon: Users, path: '/reports/customers', key: 'report-customers' },
      { label: '廠商回金', icon: HandCoins, path: '/reports/vendors', key: 'report-vendors' },
      { label: '文件測試', icon: FileImage, path: '/reports/documents', key: 'report-documents' },
    ],
  },
  { label: '使用者管理', icon: User,      path: '/users',    key: 'users'    },
  { label: '租戶管理',   icon: Building2, path: '/tenants',  key: 'tenants'  },
  { label: '系統設定',   icon: Settings,  path: '/settings', key: 'settings' },
]

const getNavigationByRole = (role?: string): NavEntry[] => {
  if (role === 'driver') {
    return allNavigation.filter(e => !isGroup(e) && ['dashboard', 'orders', 'my-expenses', 'my-report'].includes(e.key)) as NavItem[]
  }
  if (role === 'tenant_admin') {
    return allNavigation.filter(e =>
      (!isGroup(e) && ['dashboard','orders','drivers','customers','expenses','batch-orders'].includes(e.key)) ||
      (isGroup(e) && e.key === 'reports')
    )
  }
  if (role === 'super_admin') {
    return allNavigation
  }
  if (role === 'tenant_manager') {
    return allNavigation.filter(e =>
      !isGroup(e) && ['dashboard','orders','drivers','customers','expenses','batch-orders'].includes(e.key)
    )
  }
  // others: reports without 文件測試
  return allNavigation.map(e => isGroup(e) && e.key === 'reports'
    ? { ...e, children: e.children.filter(c => c.key !== 'report-documents') }
    : e
  )
}

function NavBtn({ item, isActive, onClick }: { item: NavItem; isActive: boolean; onClick: () => void }) {
  const Icon = item.icon
  return (
    <button
      onClick={onClick}
      className={clsx(
        'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-150',
        isActive
          ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/50 dark:text-blue-400'
          : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700',
      )}
    >
      <Icon className="h-5 w-5 shrink-0" />
      <span>{item.label}</span>
    </button>
  )
}

function GroupBtn({ group, location, navigate }: { group: NavGroup; location: { pathname: string }; navigate: (p: string) => void }) {
  const Icon = group.icon
  const anyActive = group.children.some(c => location.pathname.startsWith(c.path))
  const [open, setOpen] = useState(anyActive)

  useEffect(() => { if (anyActive) setOpen(true) }, [anyActive])

  return (
    <li>
      <button
        onClick={() => setOpen(o => !o)}
        className={clsx(
          'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-150',
          anyActive
            ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/50 dark:text-blue-400'
            : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700',
        )}
      >
        <Icon className="h-5 w-5 shrink-0" />
        <span className="flex-1 text-left">{group.label}</span>
        <ChevronDown className={clsx('h-4 w-4 shrink-0 transition-transform duration-200', open && 'rotate-180')} />
      </button>
      {open && (
        <ul className="mt-1 ml-4 space-y-1 border-l border-gray-200 dark:border-gray-700 pl-3">
          {group.children.map(child => {
            const isActive = location.pathname.startsWith(child.path)
            return (
              <li key={child.path}>
                <NavBtn item={child} isActive={isActive} onClick={() => navigate(child.path)} />
              </li>
            )
          })}
        </ul>
      )}
    </li>
  )
}

export default function Layout() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuthStore()
  const [tenant, setTenant] = useState<Tenant | null>(null)

  useEffect(() => {
    const s = localStorage.getItem('tenant')
    if (s) { try { setTenant(JSON.parse(s)) } catch {} }
  }, [])

  const enableBatchOrders = tenant?.form_config?.enable_batch_orders ?? false
  const visibleItems = getNavigationByRole(user?.role).filter(e =>
    isGroup(e) || e.key !== 'batch-orders' || enableBatchOrders
  )

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
      <div className="flex flex-1 overflow-hidden">
        <aside className="hidden lg:flex w-48 flex-col border-r border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
          <div className="flex h-16 items-center px-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600">
                <CarLogo className="h-5 w-5 text-white" />
              </div>
              <span className="text-xl font-bold text-gray-900 dark:text-white">
                {tenant?.short_name || 'Dispatch Tango'}
              </span>
            </div>
          </div>

          <nav className="flex-1 overflow-y-auto p-4">
            <ul className="space-y-1">
              {visibleItems.map((entry) => {
                if (isGroup(entry)) {
                  return <GroupBtn key={entry.key} group={entry} location={location} navigate={navigate} />
                }
                const isActive = location.pathname === entry.path || location.pathname.startsWith(entry.path + '/')
                return (
                  <li key={entry.path}>
                    <NavBtn item={entry} isActive={isActive} onClick={() => navigate(entry.path)} />
                  </li>
                )
              })}
            </ul>
          </nav>
        </aside>

        <div className="flex-1 flex flex-col min-w-0">
          <Header />
          <main className="flex-1 overflow-y-auto p-1 sm:p-4 md:p-6 pb-20 lg:pb-6">
            <Outlet />
          </main>
        </div>
      </div>
      <BottomNav />
    </div>
  )
}
